import { z } from 'zod';

import { MESSAGE } from '@/lib/constants';

// ─── Amount fields ────────────────────────────────────────────────────────────
// Native <input> values arrive as strings; the caller coerces to numbers via
// `toAmount`/`toOptionalAmount` in `./helpers` before hitting the API.

function optionalAmount() {
  return z.string().trim().superRefine((val, ctx) => {
    if (val === '') return;
    const n = Number(val);
    if (!Number.isFinite(n) || n < 0) {
      ctx.addIssue({ code: 'custom', message: 'Enter a valid amount' });
    }
  });
}

// ─── Shared invoice fields (spread into add + edit) ──────────────────────────

export const invoiceFieldsSchema = {
  totalAmount: z.string().trim().superRefine((val, ctx) => {
    const n = Number(val);
    if (val === '' || !Number.isFinite(n) || n <= 0) {
      ctx.addIssue({ code: 'custom', message: 'Enter a valid amount' });
    }
  }),
  subtotal: optionalAmount(),
  tax: optionalAmount(),
  discount: optionalAmount(),
  dueDate: z.string().trim().optional(),
  showTaxBreakdown: z.boolean(),
  offerAllMethods: z.boolean(),
  selectedMethodIds: z.array(z.string()),
};

export type InvoiceFieldsValues = {
  totalAmount: string;
  subtotal: string;
  tax: string;
  discount: string;
  dueDate?: string;
  showTaxBreakdown: boolean;
  offerAllMethods: boolean;
  selectedMethodIds: string[];
};

function refinePaymentMethods(
  val: { offerAllMethods: boolean; selectedMethodIds: string[] },
  ctx: z.RefinementCtx,
) {
  if (!val.offerAllMethods && val.selectedMethodIds.length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: ['selectedMethodIds'],
      message: 'Select at least one payment method',
    });
  }
}

// ─── Add invoice (business-wide page — needs a job picker) ───────────────────

export const addInvoiceSchema = z
  .object({
    jobId: z.string().trim().min(1, { message: MESSAGE.REQUIRED_FIELD }),
    ...invoiceFieldsSchema,
  })
  .superRefine(refinePaymentMethods);

export type AddInvoiceInput = z.infer<typeof addInvoiceSchema>;

// ─── Edit invoice (detail modal) ─────────────────────────────────────────────

export const editInvoiceSchema = z
  .object(invoiceFieldsSchema)
  .superRefine(refinePaymentMethods);

export type EditInvoiceInput = z.infer<typeof editInvoiceSchema>;
